import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import T from "../styles/tokens";
import { PFLogo } from "../components/UI";
import * as api from "../utils/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

// ─── Local icons ─────────────────────────────────────────────────────────────
const Ic = {
  Back: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3L5 8l5 5"/></svg>,
  Shield: () => <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2L3 5v4c0 4 2.8 6.8 6 8 3.2-1.2 6-4 6-8V5L9 2z"/></svg>,
  Check: () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7.5l2.5 2.5L11 5"/></svg>,
  X: () => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6"/></svg>,
  Alert: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M8 2L1.5 13h13L8 2z"/><path d="M8 6.5v3M8 11.5v.01"/></svg>,
  Users: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="5" r="2.5"/><path d="M1 13c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5"/><circle cx="12" cy="5.5" r="1.8"/><path d="M15 13c0-1.8-1.3-3.2-3-3.7"/></svg>,
  Mail: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="14" height="10" rx="2"/><path d="M1 5l7 4 7-4"/></svg>,
  Device: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="12" height="9" rx="1.5"/><path d="M5 14h6M8 11v3"/></svg>,
  License: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="1" width="12" height="14" rx="2"/><path d="M5 5h6M5 8h4M5 11h2"/></svg>,
  Link: () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a3 3 0 004 .5l1.5-1.5a3 3 0 00-4.2-4.2L6 4"/><path d="M8 6a3 3 0 00-4-.5L2.5 7a3 3 0 004.2 4.2L8 10"/></svg>,
  Refresh: ({ spinning } = {}) => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={spinning ? { animation: "pfSpin 1s linear infinite" } : undefined}><path d="M1 7a6 6 0 0111-3M13 7a6 6 0 01-11 3"/><path d="M1 2v4h4M13 12V8H9"/></svg>,
  TrendUp: () => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 9l3.5-3.5L7 8l4-5"/><path d="M8 3h3v3"/></svg>,
};

// ─── Local sub-components ────────────────────────────────────────────────────

const STATUS_MAP = {
  connected: { l: "Connected", bg: T.greenBg, c: T.green },
  healthy: { l: "Healthy", bg: T.greenBg, c: T.green },
  active: { l: "Active", bg: T.greenBg, c: T.green },
  warning: { l: "Warning", bg: T.amberBg, c: T.amber },
  critical: { l: "Critical", bg: T.redBg, c: T.red },
  info: { l: "Info", bg: "#F0EAFF", c: T.purple },
  missing: { l: "Missing", bg: T.redBg, c: T.red },
  blocked: { l: "Blocked", bg: T.greenBg, c: T.green },
  unknown: { l: "Unknown", bg: T.gray100, c: T.gray400 },
};

const Badge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.info;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 99, background: s.bg, color: s.c, fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", fontFamily: T.font, whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.c }} />
      {s.l}
    </span>
  );
};

const ScoreRing = ({ score, max, size = 90, stroke = 7 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? score / max : 0;
  const offset = circ * (1 - pct);
  const color = pct >= 0.7 ? T.green : pct >= 0.5 ? T.amber : T.red;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.gray200} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 9, color: T.gray400, fontFamily: T.font }}>/{max}</span>
      </div>
    </div>
  );
};

const ProgressBar = ({ value, max, color, height = 6, showLabel = false }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
      <div style={{ flex: 1, background: T.gray200, borderRadius: 99, height, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color || T.purple, borderRadius: 99, transition: "width 0.6s ease" }} />
      </div>
      {showLabel && <span style={{ fontSize: 11, fontWeight: 600, color: T.charcoal, fontFamily: T.font, minWidth: 36 }}>{pct}%</span>}
    </div>
  );
};

const SectionCard = ({ icon, title, badge, children, style: sx }) => (
  <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, overflow: "hidden", ...sx }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: `1px solid ${T.gray100}` }}>
      <span style={{ color: T.purple, display: "inline-flex" }}>{icon}</span>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.indigo, fontFamily: T.font, flex: 1 }}>{title}</h3>
      {badge}
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);

const MetricRow = ({ label, value, sub, color }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "6px 0", borderBottom: `1px solid ${T.gray100}` }}>
    <span style={{ fontSize: 12.5, color: T.charcoal, fontFamily: T.font }}>{label}</span>
    <div style={{ textAlign: "right" }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: color || T.indigo, fontFamily: T.font }}>{value}</span>
      {sub && <span style={{ fontSize: 10.5, color: T.gray400, fontFamily: T.font, marginLeft: 6 }}>{sub}</span>}
    </div>
  </div>
);

const Tab = ({ active, label, onClick, icon }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", gap: 6,
    padding: "8px 16px", borderRadius: 99, border: "none", cursor: "pointer",
    background: active ? T.purple : "transparent",
    color: active ? T.white : T.charcoal,
    fontSize: 12, fontWeight: 600, fontFamily: T.font,
    transition: "all 0.15s",
  }}>
    {icon}{label}
  </button>
);

const EmptyHint = ({ note }) => (
  <div style={{ fontSize: 12, color: T.gray400, fontFamily: T.font, fontStyle: "italic", padding: "16px 0", textAlign: "center" }}>{note}</div>
);

const SectionErrors = ({ errors }) => {
  if (!errors || errors.length === 0) return null;
  return (
    <div style={{ marginTop: 10, padding: "8px 12px", background: T.gray100, borderRadius: T.radiusSm, fontSize: 11, color: T.gray400, fontFamily: T.font }}>
      Some data unavailable:
      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
        {errors.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    </div>
  );
};

// ─── Tab content ─────────────────────────────────────────────────────────────

const OverviewTab = ({ health }) => {
  const alerts = health.alerts || [];
  const critCount = alerts.filter((a) => a.severity === "critical").length;
  const warnCount = alerts.filter((a) => a.severity === "warning").length;
  const infoCount = alerts.filter((a) => a.severity === "info").length;
  const sec = health.security || {};
  const dev = health.devices || {};

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {[
          { label: "Overall", value: critCount > 0 ? "Action Needed" : warnCount > 0 ? "Review" : "Healthy", color: critCount > 0 ? T.red : warnCount > 0 ? T.amber : T.green, bg: critCount > 0 ? T.redBg : warnCount > 0 ? T.amberBg : T.greenBg },
          { label: "Critical", value: critCount, color: critCount > 0 ? T.red : T.green, bg: critCount > 0 ? T.redBg : T.greenBg },
          { label: "Warnings", value: warnCount, color: warnCount > 0 ? T.amber : T.green, bg: warnCount > 0 ? T.amberBg : T.greenBg },
          { label: "Info", value: infoCount, color: T.purple, bg: "#F0EAFF" },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, borderRadius: T.radius, padding: "16px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: s.color, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: typeof s.value === "number" ? 28 : 16, fontWeight: 700, color: s.color, fontFamily: T.font }}>{s.value}</div>
          </div>
        ))}
      </div>

      <SectionCard icon={<Ic.Alert />} title="Active Alerts" badge={<span style={{ fontSize: 11, color: T.gray400, fontFamily: T.font }}>{alerts.length} items</span>}>
        {alerts.length === 0 ? (
          <EmptyHint note="No alerts — tenant looks healthy." />
        ) : (
          <div style={{ display: "grid", gap: 2 }}>
            {alerts.map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: i < alerts.length - 1 ? `1px solid ${T.gray100}` : "none" }}>
                <Badge status={a.severity} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, color: T.charcoal, fontFamily: T.font, fontWeight: 500 }}>{a.message}</div>
                  <div style={{ fontSize: 11, color: T.gray400, fontFamily: T.font, marginTop: 2 }}>{a.category} · {a.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 20, display: "flex", alignItems: "center", gap: 16 }}>
          {sec.secureScore ? (
            <>
              <ScoreRing score={sec.secureScore.current} max={sec.secureScore.max} size={72} stroke={6} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font }}>Secure Score</div>
                <div style={{ fontSize: 12, color: sec.secureScore.change >= 0 ? T.green : T.red, fontWeight: 600, fontFamily: T.font, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                  <Ic.TrendUp /> {sec.secureScore.change >= 0 ? "+" : ""}{sec.secureScore.change} in {sec.secureScore.period}
                </div>
              </div>
            </>
          ) : (
            <EmptyHint note="Secure Score unavailable" />
          )}
        </div>
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font, marginBottom: 8 }}>MFA Adoption</div>
          {sec.mfa ? (
            <>
              <div style={{ fontSize: 26, fontWeight: 700, color: sec.mfa.percentage >= 90 ? T.green : T.amber, fontFamily: T.font }}>{sec.mfa.percentage}%</div>
              <div style={{ fontSize: 11, color: T.charcoal, fontFamily: T.font, marginTop: 4 }}>{sec.mfa.enabled}/{sec.mfa.total} users</div>
              <ProgressBar value={sec.mfa.enabled} max={sec.mfa.total} color={sec.mfa.percentage >= 90 ? T.green : T.amber} height={4} />
            </>
          ) : (
            <EmptyHint note="MFA data unavailable" />
          )}
        </div>
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font, marginBottom: 8 }}>Device Compliance</div>
          {dev.total > 0 ? (
            <>
              <div style={{ fontSize: 26, fontWeight: 700, color: dev.complianceRate >= 90 ? T.green : T.amber, fontFamily: T.font }}>{dev.complianceRate}%</div>
              <div style={{ fontSize: 11, color: T.charcoal, fontFamily: T.font, marginTop: 4 }}>{dev.compliant}/{dev.total} devices</div>
              <ProgressBar value={dev.compliant} max={dev.total} color={dev.complianceRate >= 90 ? T.green : T.amber} height={4} />
            </>
          ) : (
            <EmptyHint note="No managed devices" />
          )}
        </div>
      </div>
    </div>
  );
};

const ConnectionTab = ({ health }) => {
  const conn = health.connection || {};
  const policy = conn.crossTenantPolicy || {};
  const guests = conn.guestAccounts || [];
  const roles = conn.roles || [];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionCard icon={<Ic.Link />} title="Cross-Tenant Access Policy" badge={<Badge status={policy.status === "active" ? "active" : policy.status === "missing" ? "missing" : "unknown"} />}>
        <div style={{ display: "grid", gap: 4 }}>
          <MetricRow label="Inbound Trust" value={policy.status === "active" ? "Active" : policy.status === "missing" ? "Missing" : "—"} color={policy.status === "active" ? T.green : policy.status === "missing" ? T.red : T.gray400} />
          <MetricRow label="MFA Trust" value={policy.mfaTrust ? "Enabled" : "Disabled"} color={policy.mfaTrust ? T.green : T.gray400} sub={!policy.mfaTrust ? "P1 required" : null} />
          <MetricRow label="Compliant Device Trust" value={policy.compliantDeviceTrust ? "Enabled" : "Disabled"} color={policy.compliantDeviceTrust ? T.green : T.gray400} />
          <MetricRow label="Auto Redemption" value={policy.autoRedemption ? "Enabled" : "Disabled"} color={policy.autoRedemption ? T.green : T.gray400} />
        </div>
      </SectionCard>

      <SectionCard icon={<Ic.Users />} title="Pathfinder Guest Accounts" badge={<span style={{ fontSize: 11, color: T.gray400, fontFamily: T.font }}>{guests.length} accounts</span>}>
        {guests.length === 0 ? (
          <EmptyHint note="No Pathfinder guest accounts on record." />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {guests.map((g, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: T.gray100, borderRadius: T.radiusSm }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg, ${T.gradStart}, ${T.gradEnd})`, display: "flex", alignItems: "center", justifyContent: "center", color: T.white, fontSize: 12, fontWeight: 700, fontFamily: T.font }}>{(g.displayName || g.email || "?").charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{g.displayName}</div>
                  <div style={{ fontSize: 11, color: T.gray400, fontFamily: T.font }}>{g.email}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Badge status={g.status} />
                  {g.lastSignIn && <div style={{ fontSize: 10, color: T.gray400, fontFamily: T.font, marginTop: 4 }}>Last sign-in: {fmtDate(g.lastSignIn)}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard icon={<Ic.Shield />} title="Directory Role Assignments" badge={<span style={{ fontSize: 11, color: T.gray400, fontFamily: T.font }}>{roles.filter((r) => r.assigned).length}/{roles.length} active</span>}>
        {roles.length === 0 ? (
          <EmptyHint note="No role assignments on record." />
        ) : (
          <div style={{ display: "grid", gap: 2 }}>
            {roles.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < roles.length - 1 ? `1px solid ${T.gray100}` : "none" }}>
                <span style={{ color: r.assigned ? T.green : T.red, flexShrink: 0, display: "inline-flex" }}>{r.assigned ? <Ic.Check /> : <Ic.X />}</span>
                <span style={{ fontSize: 12.5, color: r.assigned ? T.charcoal : T.red, fontFamily: T.font, flex: 1, fontWeight: r.assigned ? 400 : 600 }}>{r.name}</span>
                <span style={{ fontSize: 11, color: T.gray400, fontFamily: T.font }}>{r.user}</span>
                {!r.assigned && <Badge status="missing" />}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionErrors errors={conn._errors} />
    </div>
  );
};

const SecurityTab = ({ health }) => {
  const sec = health.security || {};
  const ss = sec.secureScore;
  const mfa = sec.mfa;
  const ga = sec.globalAdmins;
  const ca = sec.conditionalAccess;
  const rs = sec.riskySignIns;
  const ru = sec.riskyUsers;
  const la = sec.legacyAuth || {};

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard icon={<Ic.Shield />} title="Microsoft Secure Score">
          {ss ? (
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <ScoreRing score={ss.current} max={ss.max} />
              <div>
                <div style={{ fontSize: 12, color: ss.change >= 0 ? T.green : T.red, fontWeight: 600, fontFamily: T.font, display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                  <Ic.TrendUp /> {ss.change >= 0 ? "+" : ""}{ss.change} in {ss.period}
                </div>
                <div style={{ fontSize: 11.5, color: T.charcoal, fontFamily: T.font, lineHeight: 1.5 }}>
                  {ss.current >= 70 ? "Strong baseline — keep monitoring." : ss.current >= 50 ? "Above average. Focus on CA policies to improve." : "Below recommended. Several quick wins available."}
                </div>
              </div>
            </div>
          ) : <EmptyHint note="Secure Score unavailable" />}
        </SectionCard>

        <SectionCard icon={<Ic.Shield />} title="MFA Adoption" badge={mfa ? <Badge status={mfa.percentage >= 95 ? "healthy" : "warning"} /> : null}>
          {mfa ? (
            <>
              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 36, fontWeight: 700, color: mfa.percentage >= 90 ? T.green : T.amber, fontFamily: T.font }}>{mfa.percentage}%</div>
                <div style={{ fontSize: 11.5, color: T.charcoal, fontFamily: T.font }}>{mfa.enabled} enabled · {mfa.disabled} without MFA</div>
              </div>
              <ProgressBar value={mfa.enabled} max={mfa.total} color={mfa.percentage >= 90 ? T.green : T.amber} height={8} showLabel />
              {mfa.disabled > 0 && (
                <div style={{ marginTop: 10, padding: "8px 12px", background: T.amberBg, borderRadius: T.radiusSm, fontSize: 11.5, color: T.amber, fontWeight: 500, fontFamily: T.font }}>
                  {mfa.disabled} users without MFA — recommend enforcing via Conditional Access
                </div>
              )}
            </>
          ) : <EmptyHint note="MFA registration data unavailable (requires UserAuthenticationMethod.Read.All)" />}
        </SectionCard>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard icon={<Ic.Users />} title="Global Administrators" badge={ga ? <Badge status={ga.count > 4 ? "warning" : "info"} /> : null}>
          {ga ? (
            <>
              <div style={{ fontSize: 28, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 8 }}>{ga.count}</div>
              {ga.accounts && ga.accounts.length > 0 && (
                <div style={{ display: "grid", gap: 4 }}>
                  {ga.accounts.slice(0, 6).map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: T.charcoal, fontFamily: T.font, padding: "4px 0", borderBottom: `1px solid ${T.gray100}` }}>{a}</div>
                  ))}
                </div>
              )}
              {ga.warning && <div style={{ marginTop: 10, fontSize: 11, color: T.amber, fontFamily: T.font, fontWeight: 500 }}>{ga.warning}</div>}
            </>
          ) : <EmptyHint note="Global admin data unavailable" />}
        </SectionCard>

        <SectionCard icon={<Ic.Shield />} title="Conditional Access Policies">
          {ca ? (
            <>
              <MetricRow label="Total Policies" value={ca.total} />
              <MetricRow label="Enforced" value={ca.enforced} color={T.green} />
              <MetricRow label="Report-Only" value={ca.reportOnly} color={T.amber} />
              {ca.policies && ca.policies.length > 0 && (
                <div style={{ marginTop: 10, display: "grid", gap: 4 }}>
                  {ca.policies.map((p, i) => (
                    <div key={i} style={{ fontSize: 11.5, color: T.charcoal, fontFamily: T.font, padding: "4px 10px", background: T.gray100, borderRadius: T.radiusSm }}>{p}</div>
                  ))}
                </div>
              )}
            </>
          ) : <EmptyHint note="Conditional Access data unavailable (requires Policy.Read.All)" />}
        </SectionCard>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <SectionCard icon={<Ic.Alert />} title="Risky Users" badge={rs && rs.high > 0 ? <Badge status="critical" /> : <Badge status="info" />}>
          {rs ? (
            <>
              <div style={{ fontSize: 28, fontWeight: 700, color: rs.high > 0 ? T.red : T.indigo, fontFamily: T.font, marginBottom: 8 }}>{rs.last30Days}</div>
              <MetricRow label="High Risk" value={rs.high} color={T.red} />
              <MetricRow label="Medium Risk" value={rs.medium} color={T.amber} />
              <MetricRow label="Low Risk" value={rs.low} color={T.charcoal} />
            </>
          ) : <EmptyHint note="Risk data unavailable (requires IdentityRiskyUser.Read.All)" />}
        </SectionCard>

        <SectionCard icon={<Ic.Shield />} title="Additional Security">
          <MetricRow label="Legacy Authentication" value={la.blocked ? "Blocked" : "Allowed"} color={la.blocked ? T.green : T.amber} />
          {ru && <MetricRow label="Risky Users Flagged" value={ru.count} color={ru.count > 0 ? T.red : T.green} />}
          {ru && ru.users && ru.users.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 4 }}>
              {ru.users.slice(0, 5).map((u, i) => (
                <div key={i} style={{ fontSize: 11.5, color: T.red, fontFamily: T.font, padding: "4px 10px", background: T.redBg, borderRadius: T.radiusSm, fontWeight: 500 }}>{u}</div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <SectionErrors errors={sec._errors} />
    </div>
  );
};

const LicensingTab = ({ health }) => {
  const lic = health.licensing || {};
  const skus = lic.skus || [];
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {[
          { label: "Total Licenses", value: lic.total ?? "—", color: T.indigo },
          { label: "Assigned", value: lic.assigned ?? "—", color: T.green },
          { label: "Unassigned", value: lic.unassigned ?? "—", color: lic.unassigned > 0 ? T.amber : T.green },
          { label: "Users w/o License", value: lic.usersWithoutLicense ?? "—", color: lic.usersWithoutLicense > 0 ? T.amber : T.green },
        ].map((s, i) => (
          <div key={i} style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: "16px 18px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, fontFamily: T.font }}>{s.value}</div>
          </div>
        ))}
      </div>

      <SectionCard icon={<Ic.License />} title="License Breakdown by SKU">
        {skus.length === 0 ? (
          <EmptyHint note="No licenses or data unavailable." />
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            {skus.map((sku, i) => (
              <div key={i}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{sku.name}</span>
                  <span style={{ fontSize: 11.5, color: T.charcoal, fontFamily: T.font }}>
                    {sku.assigned}/{sku.purchased} assigned
                    {sku.unassigned > 0 && <span style={{ color: T.amber, fontWeight: 600, marginLeft: 6 }}>({sku.unassigned} unused)</span>}
                  </span>
                </div>
                <ProgressBar value={sku.assigned} max={sku.purchased} color={sku.unassigned > 2 ? T.amber : T.green} height={6} />
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionErrors errors={lic._errors} />
    </div>
  );
};

const EmailTab = ({ health }) => {
  const email = health.email || {};
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {[
          { label: "Mailboxes", value: email.totalMailboxes ?? "—", color: T.indigo },
          { label: "Shared", value: email.sharedMailboxes ?? "—", color: T.purple },
          { label: "Near Quota", value: (email.mailboxesNearQuota || []).length, color: (email.mailboxesNearQuota || []).length > 0 ? T.amber : T.green },
          { label: "Mail Flow", value: email.mailFlow === "healthy" ? "Healthy" : email.mailFlow === "unknown" ? "—" : "Issues", color: email.mailFlow === "healthy" ? T.green : T.gray400 },
        ].map((s, i) => (
          <div key={i} style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: "16px 18px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, fontFamily: T.font }}>{s.value}</div>
          </div>
        ))}
      </div>

      <SectionCard icon={<Ic.Mail />} title="Mailboxes Near Quota" badge={(email.mailboxesNearQuota || []).length > 0 ? <Badge status="warning" /> : <Badge status="healthy" />}>
        {(email.mailboxesNearQuota || []).length > 0 ? (
          <div style={{ display: "grid", gap: 8 }}>
            {email.mailboxesNearQuota.map((m, i) => (
              <div key={i} style={{ padding: "10px 14px", background: T.amberBg, borderRadius: T.radiusSm }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: T.amber, fontFamily: T.font, marginBottom: 4 }}>{m.user}</div>
                <div style={{ fontSize: 11.5, color: T.charcoal, fontFamily: T.font, marginBottom: 6 }}>{m.used} / {m.quota}</div>
                <ProgressBar value={m.percentage} max={100} color={m.percentage > 90 ? T.red : T.amber} height={5} showLabel />
              </div>
            ))}
          </div>
        ) : (
          <EmptyHint note="All mailboxes within healthy limits (or quota data unavailable)" />
        )}
      </SectionCard>

      {email._note && (
        <div style={{ padding: "10px 14px", background: T.gray100, borderRadius: T.radiusSm, fontSize: 11.5, color: T.gray400, fontFamily: T.font, fontStyle: "italic" }}>
          {email._note}
        </div>
      )}
      <SectionErrors errors={email._errors} />
    </div>
  );
};

const DevicesTab = ({ health }) => {
  const dev = health.devices || {};
  const stale = dev.staleDevices || { count: 0, devices: [] };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {[
          { label: "Total Devices", value: dev.total ?? "—", color: T.indigo },
          { label: "Compliant", value: dev.compliant ?? "—", color: T.green },
          { label: "Non-Compliant", value: dev.nonCompliant ?? "—", color: (dev.nonCompliant || 0) > 0 ? T.red : T.green },
          { label: "Stale (30d+)", value: stale.count, color: stale.count > 0 ? T.amber : T.green },
        ].map((s, i) => (
          <div key={i} style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: "16px 18px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.color, fontFamily: T.font }}>{s.value}</div>
          </div>
        ))}
      </div>

      {dev.total > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <SectionCard icon={<Ic.Device />} title="Device Compliance" badge={<Badge status={dev.complianceRate >= 90 ? "healthy" : "warning"} />}>
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 16 }}>
              <ScoreRing score={Math.round(dev.complianceRate)} max={100} size={80} stroke={6} />
              <div>
                <div style={{ fontSize: 12, color: T.charcoal, fontFamily: T.font }}>{dev.compliant} compliant of {dev.total} managed</div>
                <div style={{ fontSize: 11, color: T.gray400, fontFamily: T.font, marginTop: 4 }}>{dev.notEvaluated} not yet evaluated</div>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={<Ic.Device />} title="Operating System Breakdown">
            {(dev.osBreakdown || []).length === 0 ? (
              <EmptyHint note="No OS data" />
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {dev.osBreakdown.map((os, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: T.charcoal, fontFamily: T.font }}>{os.os}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{os.count} ({os.percentage}%)</span>
                    </div>
                    <ProgressBar value={os.count} max={dev.total} color={T.purple} height={4} />
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      ) : (
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 32, textAlign: "center", color: T.gray400, fontFamily: T.font, fontSize: 13 }}>
          No managed devices found (Intune may not be deployed on this tenant).
        </div>
      )}

      {stale.count > 0 && (
        <SectionCard icon={<Ic.Alert />} title={`Stale Devices (${stale.threshold || "30 days"}+ inactive)`} badge={<Badge status="warning" />}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {stale.devices.map((d, i) => (
              <span key={i} style={{ padding: "5px 12px", borderRadius: T.radiusSm, background: T.amberBg, color: T.amber, fontSize: 12, fontWeight: 500, fontFamily: T.font }}>{d}</span>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionErrors errors={dev._errors} />
    </div>
  );
};

// ─── Main ───────────────────────────────────────────────────────────────────

const TABS = [
  { id: "overview", label: "Overview", icon: <Ic.Alert /> },
  { id: "connection", label: "Connection", icon: <Ic.Link /> },
  { id: "security", label: "Security", icon: <Ic.Shield /> },
  { id: "licensing", label: "Licensing", icon: <Ic.License /> },
  { id: "email", label: "Email", icon: <Ic.Mail /> },
  { id: "devices", label: "Devices", icon: <Ic.Device /> },
];

const LoadingSkeleton = () => (
  <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px", display: "grid", gap: 16 }}>
    {[0, 1, 2].map((i) => (
      <div key={i} style={{ background: T.white, border: `1px solid ${T.gray200}`, borderRadius: T.radius, padding: 24, height: i === 0 ? 120 : 180, display: "flex", alignItems: "center", justifyContent: "center", color: T.gray400, fontFamily: T.font, fontSize: 13 }}>
        Loading…
      </div>
    ))}
  </div>
);

export default function TenantHealth() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [client, setClient] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [c, h] = await Promise.all([
        api.getClient(id),
        api.getTenantHealth(id, { refresh }),
      ]);
      setClient(c);
      setHealth(h);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const overallStatus = !health ? "unknown" :
    (health.alerts || []).some((a) => a.severity === "critical") ? "critical" :
    (health.alerts || []).some((a) => a.severity === "warning") ? "warning" : "healthy";

  return (
    <div style={{ minHeight: "100vh", background: T.gray100, fontFamily: T.font, marginLeft: -36, marginRight: -36, marginTop: -28, marginBottom: -28 }}>
      {/* Header */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.gray200}`, padding: "16px 32px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, maxWidth: 1200, margin: "0 auto" }}>
          <button onClick={() => navigate(`/dashboard/clients/${id}`)} style={{ background: "none", border: "none", cursor: "pointer", color: T.purple, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, fontFamily: T.font }}>
            <Ic.Back /> Back
          </button>
          <PFLogo size={32} />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.indigo }}>{client?.name || "Loading…"}</h1>
              {health && <Badge status={overallStatus} />}
            </div>
            {client && (
              <div style={{ fontSize: 11.5, color: T.gray400, marginTop: 2 }}>{client.domain} · {client.template?.name || client.templateId} · Connected {fmtDate(client.connectedAt)}</div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10.5, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>Last Health Check</div>
            <div style={{ fontSize: 12, color: T.charcoal, fontWeight: 500, marginTop: 2 }}>
              {health?.fetchedAt ? fmtDateTime(health.fetchedAt) : "—"}
              {health?.cached && <span style={{ color: T.gray400, marginLeft: 6 }}>(cached)</span>}
            </div>
          </div>
          <button onClick={() => load({ refresh: true })} disabled={refreshing} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: T.radiusSm, border: `1px solid ${T.gray200}`, background: T.white, cursor: refreshing ? "wait" : "pointer", fontSize: 12, fontWeight: 600, color: T.purple, fontFamily: T.font, opacity: refreshing ? 0.6 : 1 }}>
            <Ic.Refresh spinning={refreshing} /> {refreshing ? "Running…" : "Run Check"}
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.gray200}`, padding: "8px 32px" }}>
        <div style={{ display: "flex", gap: 4, maxWidth: 1200, margin: "0 auto" }}>
          {TABS.map((t) => (
            <Tab key={t.id} active={tab === t.id} label={t.label} icon={t.icon} onClick={() => setTab(t.id)} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px" }}>
        {error && (
          <div style={{ background: T.redBg, color: T.red, padding: "12px 16px", borderRadius: T.radiusSm, fontSize: 13, fontFamily: T.font, marginBottom: 16 }}>
            Failed to load health data: {error}
          </div>
        )}
        {loading ? (
          <LoadingSkeleton />
        ) : health ? (
          <>
            {tab === "overview" && <OverviewTab health={health} />}
            {tab === "connection" && <ConnectionTab health={health} />}
            {tab === "security" && <SecurityTab health={health} />}
            {tab === "licensing" && <LicensingTab health={health} />}
            {tab === "email" && <EmailTab health={health} />}
            {tab === "devices" && <DevicesTab health={health} />}
          </>
        ) : null}
      </div>

      <style>{`@keyframes pfSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
